import { useCallback, useEffect, useRef, useState } from "react";

/**
 * An overlay scrollbar: transparent, over the content, visible only while
 * scrolling.
 *
 * Why a hook and not CSS. `::-webkit-scrollbar` can be styled but not made to
 * overlay — the browser reserves its width as a gutter, so a 8px bar pushes
 * the content 8px left for as long as the panel is scrollable. The one CSS
 * property that did overlay (`overflow: overlay`) was never standardised and
 * has been removed from Chrome. So: hide the native bar, and draw a thumb.
 *
 * The thumb is an absolutely positioned child INSIDE the scroll container,
 * which means it scrolls with the content — hence `top` carries `scrollTop`
 * and then adds the thumb's own travel. Put it outside and it would need the
 * container to be `position: relative` with `overflow: visible`, which is
 * exactly what a scroll container cannot be.
 *
 * Returns null while there is nothing to scroll, so the caller renders no
 * thumb at all on a short menu.
 */

export type OverlayThumb = {
  /** Offset from the top of the scrolled content, in px. */
  top: number;
  height: number;
  /** False once scrolling has been quiet for `hideAfterMs`. */
  visible: boolean;
};

/** Below this the thumb becomes a dot nobody can see or grab. */
const MIN_THUMB = 28;

export function useOverlayScrollbar(
  ref: React.RefObject<HTMLElement | null>,
  opts?: { hideAfterMs?: number }
): OverlayThumb | null {
  const hideAfterMs = opts?.hideAfterMs ?? 900;

  const [metrics, setMetrics] = useState<{ top: number; height: number } | null>(null);
  const [visible, setVisible] = useState(false);
  const hideTimer = useRef<number | null>(null);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const { scrollHeight, clientHeight, scrollTop } = el;
    const overflow = scrollHeight - clientHeight;
    // A one-pixel overflow is rounding, not a scrollable panel.
    if (overflow <= 1 || clientHeight === 0) {
      setMetrics(null);
      return;
    }
    const height = Math.max(MIN_THUMB, Math.round((clientHeight / scrollHeight) * clientHeight));
    const travel = clientHeight - height;
    setMetrics({ top: Math.round(scrollTop + (scrollTop / overflow) * travel), height });
  }, [ref]);

  const reveal = useCallback(() => {
    setVisible(true);
    if (hideTimer.current !== null) window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => setVisible(false), hideAfterMs);
  }, [hideAfterMs]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const onScroll = () => {
      measure();
      reveal();
    };

    measure();
    el.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", measure);

    // The menu's own height changes without a scroll or a resize — a nav group
    // appears once a club turns coordinators on, and the thumb would keep the
    // length it had before.
    let observer: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(measure);
      observer.observe(el);
      // The content, not just the viewport: observing only the container
      // misses a child growing inside a fixed-height panel.
      Array.from(el.children).forEach((child) => observer!.observe(child));
    }

    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", measure);
      observer?.disconnect();
      if (hideTimer.current !== null) window.clearTimeout(hideTimer.current);
    };
  }, [ref, measure, reveal]);

  return metrics ? { ...metrics, visible } : null;
}
